import React, { useEffect } from "react";
import DeductionFairnessCheck from "@/components/DeductionFairnessCheck";

/**
 * Minimal embeddable version of the Deduction Fairness Check.
 *
 * Designed to live inside an <iframe> on third-party sites (Tenants'
 * Union blogs, Choice articles, renter community pages). No site nav,
 * no footer, no signup CTA on the page itself — the calculator
 * component already carries its own branded watermark, disclaimer and
 * Facebook share. A tiny "Powered by TenantTrail" link sits at the
 * bottom and opens in _top (breaks out of the iframe) with a utm tag
 * so we can attribute traffic back to specific embeds.
 *
 * The calculator runs 100% client-side, so embeds cost us nothing
 * beyond the static asset transfer (which is Cloudflare cached).
 */
export default function EmbedCheckADeduction() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Is this bond deduction fair? Free calculator";
    // Auto-resize: post the rendered height up to the parent window so
    // hosts can iframe without a fixed height. Optional, hosts can ignore.
    const post = () => {
      try {
        const h = document.documentElement.scrollHeight;
        window.parent && window.parent.postMessage(
          { type: "tenanttrail:embed:resize", height: h },
          "*"
        );
      } catch (_) { /* cross-origin, fine */ }
    };
    const ro = new ResizeObserver(post);
    ro.observe(document.body);
    post();
    return () => {
      ro.disconnect();
      document.title = prev;
    };
  }, []);

  // UTM-tagged link back to the canonical site. Opens at the top frame,
  // not inside the iframe.
  const homeUrl = "https://tenanttrail.net/check-a-deduction?utm_source=embed&utm_medium=iframe&utm_campaign=deduction_check";

  return (
    <div className="min-h-screen bg-[#FAF9F6] px-4 sm:px-6 py-6 sm:py-8">
      <div className="max-w-2xl mx-auto">
        <DeductionFairnessCheck shareUrl="https://tenanttrail.net/check-a-deduction" />
        <p className="text-center text-[11px] text-[#6B7280] font-mono mt-6 tracking-[0.05em]">
          Powered by{" "}
          <a
            href={homeUrl}
            target="_top"
            rel="noopener"
            className="text-[#1B6C62] hover:text-[#0D2B52] underline"
            data-testid="embed-powered-by"
          >
            TenantTrail
          </a>
          {" · free tool for Australian renters"}
        </p>
      </div>
    </div>
  );
}
